import {
  Typography,
  Box,
  Card,
  CardContent,
  Container,
  alpha,
  useTheme,
  Chip,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Divider,
} from "@mui/material";
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAttemptDetails, clearAttempt } from "../../state/slices/quizAttempt";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../components/loading/loading";
import { CheckCircle, Cancel } from "@mui/icons-material";

const QuizResults = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { currentAttempt, loading } = useSelector((state) => state.quizAttempt);

  useEffect(() => {
    dispatch(getAttemptDetails(id));
    return () => {
      dispatch(clearAttempt());
    };
  }, [id, dispatch]);

  if (loading || !currentAttempt) {
    return <Loading />;
  }

  const quiz = currentAttempt.quizId;
  if (!quiz) {
    return <Loading />;
  }
  
  const getAnswerForQuestion = (questionId) => {
    return currentAttempt.answers.find(a => {
      const answerQId = a.questionId?._id || a.questionId;
      const qId = questionId?._id || questionId;
      return answerQId?.toString() === qId?.toString();
    });
  };

  // Helper function to normalize correct answer to actual option text
  const getCorrectAnswerText = (correctAnswer, options) => {
    if (!correctAnswer && correctAnswer !== 0) return null;
    if (!options || options.length === 0) return correctAnswer?.toString();
    
    const valueStr = correctAnswer.toString().trim();
    
    // First, try to match directly with any option (case-insensitive, exact match)
    const directMatch = options.find(opt => 
      opt.toString().trim().toLowerCase() === valueStr.toLowerCase()
    );
    if (directMatch !== undefined) {
      return directMatch.toString().trim();
    }
    
    // If no direct match and value is a number, try as index
    // Most tutors think in 1-based indexing (option 1, option 2, etc.)
    if (!isNaN(valueStr)) {
      const index = parseInt(valueStr);
      // Try 1-based index first (most common: "3" means 3rd option, which is options[2])
      if (index >= 1 && index <= options.length) {
        return options[index - 1].toString().trim();
      }
      // Then try 0-based index (for programmers: "2" means options[2])
      if (index >= 0 && index < options.length) {
        return options[index].toString().trim();
      }
    }
    
    // Return original value as fallback
    return valueStr;
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 700, color: theme.palette.primary.main }}>
              {t('quiz.quizResults')}
            </Typography>
            <Typography variant="h5" sx={{ mb: 2 }}>
              {quiz.title}
            </Typography>

            <Box sx={{ mb: 4, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2 }}>
              <Typography variant="h3" align="center" sx={{ fontWeight: 700 }}>
                {currentAttempt.percentage}%
              </Typography>
              <Typography variant="body1" align="center" color="textSecondary">
                {t('quiz.score')}: {currentAttempt.score} / {quiz.totalPoints} {t('quiz.points')}
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              {t('quiz.questionReview')}
            </Typography>
            {quiz.questions?.map((question, index) => {
              const questionId = question._id || question.id;
              const answerData = getAnswerForQuestion(questionId);
              const isCorrect = answerData?.isCorrect || false;
              const userAnswer = answerData?.answer?.toString() || null;
              const isManuallyGraded = question.type === 'shortAnswer' || question.type === 'essay';
              
              return (
                <Card 
                  key={index} 
                  sx={{ 
                    mb: 3, 
                    p: 3, 
                    border: isManuallyGraded 
                      ? `2px solid ${alpha(theme.palette.primary.main, 0.2)}` 
                      : `2px solid ${isCorrect ? theme.palette.success.main : theme.palette.error.main}` 
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    {!isManuallyGraded && (
                      isCorrect ? (
                        <CheckCircle sx={{ color: theme.palette.success.main, mr: 1, fontSize: 28 }} />
                      ) : (
                        <Cancel sx={{ color: theme.palette.error.main, mr: 1, fontSize: 28 }} />
                      )
                    )}
                    <Typography variant="h6" sx={{ flex: 1 }}>
                      {index + 1}. {question.question}
                    </Typography>
                    {!isManuallyGraded && (
                      <Chip
                        label={`${answerData?.pointsEarned || 0} / ${question.points} ${t('quiz.points')}`}
                        size="small"
                        color={isCorrect ? "success" : "error"}
                      />
                    )}
                  </Box>

                  {/* Show question type and options for multiple choice */}
                  {question.type === 'multipleChoice' && question.options && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('quiz.options')}:
                      </Typography>
                      <RadioGroup value={userAnswer || ''}>
                        {(() => {
                          // Normalize correct answer to option text
                          const correctAnswerText = getCorrectAnswerText(question.correctAnswer, question.options);
                          return question.options.map((option, optIndex) => {
                            const isUserAnswer = option === userAnswer;
                            const isCorrectAnswer = option === correctAnswerText;
                            let bgColor = 'transparent';
                            let borderColor = 'transparent';
                            
                            if (isCorrectAnswer) {
                              bgColor = alpha(theme.palette.success.main, 0.1);
                              borderColor = theme.palette.success.main;
                            } else if (isUserAnswer && !isCorrect) {
                              bgColor = alpha(theme.palette.error.main, 0.1);
                              borderColor = theme.palette.error.main;
                            }
                            
                            return (
                              <Box
                                key={optIndex}
                                sx={{
                                  p: 1.5,
                                  mb: 1,
                                  borderRadius: 1,
                                  bgcolor: bgColor,
                                  border: `2px solid ${borderColor}`,
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <Radio
                                  checked={isUserAnswer}
                                  disabled
                                  sx={{ mr: 1 }}
                                />
                                <Typography variant="body1" sx={{ flex: 1 }}>
                                  {option}
                                </Typography>
                                {isCorrectAnswer && (
                                  <CheckCircle sx={{ color: theme.palette.success.main, ml: 1 }} />
                                )}
                                {isUserAnswer && !isCorrect && (
                                  <Cancel sx={{ color: theme.palette.error.main, ml: 1 }} />
                                )}
                              </Box>
                            );
                          });
                        })()}
                      </RadioGroup>
                      {!isCorrect && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 1 }}>
                          <Typography variant="body2">
                            <strong>{t('quiz.correctAnswer')}:</strong>{' '}
                            <span style={{ color: theme.palette.success.main }}>
                              {getCorrectAnswerText(question.correctAnswer, question.options) || question.correctAnswer}
                            </span>
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Show answer for true/false */}
                  {question.type === 'trueFalse' && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>{t('quiz.yourAnswer')}:</strong>{' '}
                        <span style={{ color: isCorrect ? theme.palette.success.main : theme.palette.error.main }}>
                          {userAnswer === true || userAnswer === 'true' ? t('quiz.true') : t('quiz.false')}
                        </span>
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t('quiz.correctAnswer')}:</strong>{' '}
                        <span style={{ color: theme.palette.success.main }}>
                          {question.correctAnswer === true || question.correctAnswer === 'true' ? t('quiz.true') : t('quiz.false')}
                        </span>
                      </Typography>
                    </Box>
                  )}

                  {/* Show answer for short answer and essay - no colored borders, just neutral display */}
                  {(question.type === 'shortAnswer' || question.type === 'essay') && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        <strong>{t('quiz.yourAnswer')}:</strong>
                      </Typography>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderRadius: 1,
                        mb: 2
                      }}>
                        <Typography variant="body1">
                          {userAnswer || t('quiz.noAnswer')}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        <strong>{t('quiz.correctAnswer')}:</strong>
                      </Typography>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderRadius: 1
                      }}>
                        <Typography variant="body1">
                          {question.correctAnswer?.toString() || t('quiz.noAnswer')}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {question.explanation && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body2" sx={{ fontStyle: "italic", color: theme.palette.text.secondary }}>
                        <strong>{t('quiz.explanation')}:</strong> {question.explanation}
                      </Typography>
                    </>
                  )}
                </Card>
              );
            })}

            <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "center" }}>
              <Button variant="contained" onClick={() => navigate("/quiz/student")}>
                {t('quiz.backToQuizzes')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default QuizResults;

